import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Mail, Phone, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import { companyInfo, contactInfo } from '../data/mock';
import { Link } from 'react-router-dom';

// Custom TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Footer navigation structure
  const footerLinks = {
    company: {
      title: 'Company',
      links: [
        { name: 'Our Story', href: '/about' },
        { name: 'Why Houston', href: '/why-houston' },
        { name: 'Team', href: '/team' },
        { name: 'Press & Media', href: '/press' },
        { name: 'Contact', href: '/contact' }
      ]
    },
    investments: {
      title: 'Investments',
      links: [
        { name: 'Investment Strategy', href: '/strategy' },
        { name: 'Fund Terms', href: '/fund-terms' },
        { name: 'Portfolio', href: '/portfolio' },
        { name: 'Track Record', href: '/track-record' },
        { name: 'Investment Process', href: '/investment-process' }
      ]
    },
    investors: {
      title: 'For Investors',
      links: [
        { name: 'High Net Worth Individuals', href: '/investors/high-net-worth' },
        { name: 'Family Offices', href: '/investors/family-offices' },
        { name: 'Institutional Investors', href: '/investors/institutional' },
        { name: 'FAQ', href: '/faq' }
      ]
    },
    resources: {
      title: 'Resources',
      links: [
        { name: 'Insights', href: '/insights' },
        { name: 'Case Studies', href: '/case-studies' },
        { name: 'Market Reports', href: '/market-reports' },
        { name: 'Investor Resources', href: '/resources' }
      ]
    }
  };

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Disclosures', href: '/disclosures' },
    { name: 'Cookie Settings', href: '#', onClick: true }
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/yogesh.patil.official/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61584155613629', label: 'Facebook' },
    { icon: Youtube, href: 'https://www.youtube.com/@yogeshpatil8339', label: 'YouTube' },
    { icon: TikTokIcon, href: 'https://www.tiktok.com/@yogeshpat8', label: 'TikTok' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/yogesh-patil-95468a78/', label: 'LinkedIn' }
  ];

  return (
    <footer className="relative bg-[#070D18] border-t border-white/10 overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/50 to-transparent pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div 
          className="py-12 lg:py-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          {/* Top Section - 3 Columns: Brand | Contact Us | Ready to Invest */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr] gap-10 lg:gap-20 pb-12 border-b border-white/10 mb-12"
            variants={itemVariants}
          >
            {/* Column 1: Brand */}
            <div className="text-left">
              <Link to="/" className="inline-block mb-5 -ml-3">
                <motion.img 
                  src={companyInfo.logo} 
                  alt="ASAP Capital" 
                  className="h-28 lg:h-32"
                  whileHover={{ scale: 1.02 }}
                />
              </Link>
              <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-sm">
                A Houston real estate investment firm and vertically integrated private equity fund specializing in value-add real estate investments across the Greater Houston metropolitan area since 2005. ASAP Funding is DBA ASAP Capital
              </p>
              
              {/* Social Links */}
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <motion.a 
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center group hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 transition-all duration-300"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="h-4 w-4 text-white/50 group-hover:text-[#D4AF37] transition-colors" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Column 2: Contact Us */}
            <div className="lg:pl-8">
              <h4 className="text-white font-semibold text-base mb-5">Contact Us</h4>
              <ul className="space-y-4 text-sm text-white/50">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>{contactInfo.address.city}, {contactInfo.address.state}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                  <a 
                    href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`} 
                    className="hover:text-[#D4AF37] transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                  <a 
                    href={`mailto:${contactInfo.email}`} 
                    className="hover:text-[#D4AF37] transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3: Ready to Invest */}
            <div>
              <h4 className="text-white font-semibold text-base mb-3">Ready to Invest?</h4>
              <p className="text-white/50 text-sm leading-relaxed mb-5">
                Schedule a consultation with our investor relations team.
              </p>
              <Link to="/contact">
                <button className="cta-contact-nav px-5 py-2.5 text-sm">
                  Schedule Consultation
                </button>
              </Link>
            </div>
          </motion.div>

          {/* Four Column Navigation Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-12">
            {Object.values(footerLinks).map((section) => (
              <motion.div 
                key={section.title}
                variants={itemVariants}
              >
                <h4 className="text-white font-semibold text-sm mb-4">{section.title}</h4>
                <ul className="space-y-3">
                  {section.links.map((link, index) => (
                    <motion.li 
                      key={link.name}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.03 }}
                    >
                      <Link 
                        to={link.href}
                        className="text-white/45 hover:text-[#D4AF37] transition-colors text-sm inline-block"
                      >
                        <motion.span whileHover={{ x: 3 }} className="inline-block">
                          {link.name}
                        </motion.span>
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Legal Links + Disclaimer */}
          <div className="pt-8 border-t border-white/10">
            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6">
              {legalLinks.map((link) => (
                link.onClick ? (
                  <button
                    key={link.name}
                    onClick={() => {
                      localStorage.removeItem('asap_cookie_consent');
                      window.location.reload();
                    }}
                    className="text-white/40 hover:text-[#D4AF37] transition-colors text-xs"
                  >
                    {link.name}
                  </button>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-white/40 hover:text-[#D4AF37] transition-colors text-xs"
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Disclaimer */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4 mb-6">
              <p className="text-white/30 text-xs leading-relaxed">
                <strong className="text-white/40">Important Disclosure:</strong> This website is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any securities. Any such offer will be made only by means of a Private Placement Memorandum (PPM) to qualified accredited investors. Investment involves substantial risk, including the potential loss of principal. Past performance is not indicative of future results. ASAP Capital and its affiliates do not provide tax, legal, or accounting advice.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div 
          className="py-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-white/30 text-xs">
            © {currentYear} {companyInfo.name}. All rights reserved.
          </p>
          <p className="text-white/25 text-xs">
            Houston, Texas | SEC Reg D 506(c) Offering
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
